import { FunctionComponent, useContext, useEffect, useState } from "react";
import {
  ProductionTaskEnum,
  ProductionTaskStateEnum,
  ProductionTaskType,
  ProductionVMType,
  UpdateProductionTaskType,
} from "types/ProductionType";
import PartStateButton from "components/Buttons/PartStateButton";
import TaskTableRow from "../TaskTableRow";
import { apiUpdateProductionTask } from "util/network/Productions";
import { useToast } from "contexts/ToastContext";
import AppStateContext from "contexts/AppStateContext";
import useQcDelayGate, { withDelay } from "hooks/useQcDelayGate";
import { QcDelaySubmission } from "components/Modals/QcDelayReasonDialog";
import { isDelayReasonRequired } from "util/qcDelay";

export interface ProductionPartState {
  state: ProductionTaskStateEnum | undefined;
  type: ProductionTaskEnum | undefined;
}

type PropsType = {
  project: ProductionVMType;
};

const QualityControlRow: FunctionComponent<PropsType> = ({ project }) => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const [taskStatus, setTaskStatus] = useState<ProductionPartState>({
    type: undefined,
    state: undefined,
  });
  const [tasks, setTasks] = useState<ProductionTaskType[]>(project.tasks);

  useEffect(() => {
    const qcTask = project.tasks.find((tsk) => tsk.name === "afterwork");
    const cuttingTask = project.tasks.find((tsk) => tsk.name === "production");

    if (
      cuttingTask &&
      cuttingTask.state === ProductionTaskStateEnum.DONE &&
      cuttingTask
    ) {
      setTaskStatus({
        type: ProductionTaskEnum.CAM,
        state: cuttingTask.state as ProductionTaskStateEnum,
      });
    } else if (
      cuttingTask &&
      cuttingTask.state === ProductionTaskStateEnum.DONE
    ) {
      setTaskStatus({
        type: ProductionTaskEnum.CAM,
        state: ProductionTaskStateEnum.DONE,
      });
    } else if (
      cuttingTask &&
      cuttingTask.state === ProductionTaskStateEnum.ACTIVE
    ) {
      setTaskStatus({
        type: ProductionTaskEnum.CAM,
        state: ProductionTaskStateEnum.ACTIVE,
      });
    } else if (
      cuttingTask &&
      cuttingTask.state === ProductionTaskStateEnum.ACTIVE
    ) {
      setTaskStatus({
        type: ProductionTaskEnum.REVIEW,
        state: ProductionTaskStateEnum.ACTIVE,
      });
    } else if (
      cuttingTask &&
      cuttingTask.state === ProductionTaskStateEnum.TODO
    ) {
      setTaskStatus({
        type: ProductionTaskEnum.REVIEW,
        state: ProductionTaskStateEnum.TODO,
      });
    } else {
      setTaskStatus({ type: undefined, state: undefined });
    }
  }, [project.tasks]);

  // EE-216: the actual write. Split out from handleTaskUpdate so the delay
  // gate can call it again once the dialog has been answered, with the reason
  // riding on the same request as the state change.
  const performTaskUpdate = (
    changes: Partial<ProductionTaskType>,
    delay?: QcDelaySubmission
  ) => {
    if (project) {
      const task = project.tasks.find((task) => task.id === changes.id);
      if (!task) {
        return Promise.resolve();
      }
      const updatedTask = {
        ...task,
        ...changes,
      } as ProductionTaskType;

      setTasks((prevTasks) => {
        return prevTasks.map((prevTask) => {
          if (prevTask.id === changes.id) {
            return {
              ...prevTask,
              ...changes,
            };
          } else if (changes.state) {
            if (
              changes.state === ProductionTaskStateEnum.DONE ||
              changes.state === ProductionTaskStateEnum.ACTIVE
            ) {
              if (prevTask.priority < updatedTask.priority) {
                return {
                  ...prevTask,
                  state: ProductionTaskStateEnum.DONE,
                };
              } else {
                return prevTask;
              }
            } else if (updatedTask.state === ProductionTaskStateEnum.TODO) {
              if (prevTask.priority > updatedTask.priority) {
                return {
                  ...prevTask,
                  state: ProductionTaskStateEnum.TODO,
                };
              } else {
                return prevTask;
              }
            } else {
              return prevTask;
            }
          } else {
            return prevTask;
          }
        });
      });
      let id = Number(changes.id);
      if (isNaN(id)) {
        return Promise.resolve();
      }
      const command = {
        id: id,
        name: changes.name,
        description: changes.description,
        state: changes.state,
        priority: changes.priority,
        progress: changes.progress,
        progressMax: changes.progressMax,
        deadline: changes.deadline,
        leadTime: changes.leadTime,
        workTime: changes.workTime,
        users: changes.users?.map((user) => user.id),
        machines: changes.machines?.map((machine) => machine.id),
      } as UpdateProductionTaskType;
      return apiUpdateProductionTask(token, id, withDelay(command, delay))
        .then((res) => {
          if (res) {
            addToast({
              type: "success",
              message: "Task updated",
              time: 1000,
            });
          }
        })
        .catch((error) => {
          addToast({
            type: "error",
            // EE-216: the server refuses to close the last QC step of a late
            // production without a reason. Say so, rather than the generic
            // failure - this is reachable if the dialog was bypassed.
            message: isDelayReasonRequired(error)
              ? "This QC step is past the production deadline, so a delay reason is required."
              : "Failed to update task",
            keep: true,
          });
          console.error("Failed to update task:", error);
          throw error;
        });
    }
    return Promise.resolve();
  };

  const { guard, dialog, resetNonce } = useQcDelayGate({
    project,
    onProceed: performTaskUpdate,
  });

  const handleTaskUpdate = (changes: Partial<ProductionTaskType>) => {
    // Closing the last QC step of a production that is already past its
    // deadline asks why first; everything else goes straight through.
    if (guard(changes)) {
      return;
    }
    performTaskUpdate(changes).catch(() => undefined);
  };

  const getTaskToDisplay = () => {
    const orderedTasks = project.tasks.sort((a, b) =>
      a.priority > b.priority ? 1 : a.priority < b.priority ? -1 : 0
    );

    const hasMaterialTask = project.tasks.some(
      (tsk) => tsk.description === "QC"
    );

    if (!hasMaterialTask) {
      return undefined;
    }

    const qcTaskIndex = orderedTasks.findIndex(
      (tsk) =>
        tsk.description == "QC" && tsk.state != ProductionTaskStateEnum.DONE
    );

    if (qcTaskIndex === -1) {
      return (
        orderedTasks.find((tsk) => tsk.state != ProductionTaskStateEnum.DONE) ||
        orderedTasks.reduce(
          (prev, curr) => (prev.priority < curr.priority ? curr : prev),
          project.tasks[0]
        )
      );
    }

    const qcTask = orderedTasks[qcTaskIndex];

    const beforeTask =
      qcTaskIndex > 0 ? orderedTasks[qcTaskIndex - 1] : undefined;

    if (beforeTask?.state == ProductionTaskStateEnum.DONE) {
      return qcTask;
    }

    return beforeTask ?? qcTask;
  };

  const taskToDisplay = getTaskToDisplay();

  return (
    <>
      {dialog}
      {taskToDisplay && (
        <TaskTableRow
          task={taskToDisplay}
          key={taskToDisplay.id}
          project={project}
          buttonComponent={
            <PartStateButton
              key={`${taskToDisplay.id}-${resetNonce}`}
              type={taskToDisplay.name as ProductionTaskEnum}
              state={taskToDisplay.state}
              project={project}
              onChange={handleTaskUpdate}
              task={taskToDisplay}
              title={
                taskToDisplay.description != ""
                  ? taskToDisplay.description
                  : undefined
              }
            />
          }
          taskStatus={taskStatus}
          qc
        />
      )}
    </>
  );
};
export default QualityControlRow;
